import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import Portal from './Portal';

/**
 * DatePill Component with Portal-based Calendar
 * Props:
 * - label: string
 * - value: string (yyyy-mm-dd)
 * - onChange: function(dateString)
 * - disabled: boolean
 */
export default function DatePill({ label, value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);

  // Floating UI for proper positioning
  const { refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(8),
      flip(),
      shift({ padding: 8 })
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start'
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    
    const handler = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        refs.floating.current && !refs.floating.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, refs.floating]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    
    const handler = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Parse date string without timezone conversion
  const formatted = value
    ? (() => {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      })()
    : "Select date";

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={(node) => {
          buttonRef.current = node;
          refs.setReference(node);
        }}
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: "var(--color-bg-muted)",
          border: "1px solid var(--color-border)",
          color: value
            ? "var(--color-text-primary)"
            : "var(--color-text-muted)",
        }}
      >
        <span className="text-xs text-[var(--color-text-muted)]">
          {label}
        </span>
        <span className="font-medium">{formatted}</span>
        <Calendar size={14} />
      </button>

      {/* Calendar Popover - Rendered in Portal */}
      {open && (
        <Portal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 1000,
            }}
            className="rounded-xl p-3 shadow-2xl border"
            onClick={(e) => e.stopPropagation()}
            style={{
              ...floatingStyles,
              zIndex: 1000,
              background: "var(--color-bg-canvas)",
              border: "1px solid var(--color-border)",
            }}
          >
            <CalendarGrid
              value={value}
              onSelect={(date) => {
                onChange(date);
                setOpen(false);
                buttonRef.current?.focus();
              }}
            />
          </div>
        </Portal>
      )}
    </>
  );
}

function CalendarGrid({ value, onSelect }) {
  const today = new Date();
  
  // Parse value without timezone issues
  const base = value 
    ? (() => {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      })()
    : today;

  // State for current viewing month/year
  const [viewYear, setViewYear] = useState(base.getFullYear());
  const [viewMonth, setViewMonth] = useState(base.getMonth());

  const start = new Date(viewYear, viewMonth, 1);
  const end = new Date(viewYear, viewMonth + 1, 0);

  const days = [];
  for (let i = 1; i <= end.getDate(); i++) {
    days.push(i);
  }

  const goToPreviousMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const monthName = new Date(viewYear, viewMonth).toLocaleString("default", { month: "long" });

  return (
    <div className="w-64">
      {/* Month/Year Navigation */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={goToPreviousMonth}
          className="p-1 rounded hover:bg-[var(--color-bg-muted)] transition"
        >
          <ChevronLeft size={16} />
        </button>
        
        <div className="text-sm font-medium">
          {monthName} {viewYear}
        </div>
        
        <button
          type="button"
          onClick={goToNextMonth}
          className="p-1 rounded hover:bg-[var(--color-bg-muted)] transition"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-xs text-center">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div key={d} className="text-[var(--color-text-muted)] font-medium py-1">
            {d}
          </div>
        ))}

        {Array(start.getDay() === 0 ? 6 : start.getDay() - 1)
          .fill(null)
          .map((_, i) => (
            <div key={i} />
          ))}

        {days.map((d) => {
          // Format date as YYYY-MM-DD without timezone conversion
          const yyyy = viewYear;
          const mm = String(viewMonth + 1).padStart(2, '0');
          const dd = String(d).padStart(2, '0');
          const iso = `${yyyy}-${mm}-${dd}`;
          
          const isSelected = value === iso;
          const isToday = iso === (() => {
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
          })();

          return (
            <button
              key={d}
              type="button"
              onClick={() => onSelect(iso)}
              className={`rounded-md py-1.5 transition font-medium ${
                isSelected 
                  ? 'bg-[var(--color-primary)] text-white' 
                  : isToday
                  ? 'bg-[var(--color-bg-muted)] text-[var(--color-primary)]'
                  : 'hover:bg-[var(--color-bg-muted)]'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>

      {/* Quick actions */}
      <div className="flex justify-between mt-3 pt-3 border-t text-xs" style={{ borderColor: "var(--color-border)" }}>
        <button
          type="button"
          onClick={() => {
            const now = new Date();
            const yyyy = now.getFullYear();
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const dd = String(now.getDate()).padStart(2, '0');
            onSelect(`${yyyy}-${mm}-${dd}`);
          }}
          className="text-[var(--color-primary)] hover:underline"
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => onSelect("")}
          className="text-[var(--color-text-muted)] hover:underline"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
